import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Lottie from "lottie-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ArrowRight, ChevronLeft, ChevronRight, Megaphone } from "lucide-react";

import moneyBagAnimation from "../assets/money-bag.json";
import { getUsuarioLogado } from "../services/LoginService";
import { listarAvisosVigentes } from "../services/avisoService";
import { Skeleton } from "../components/ui";
import { cn } from "../lib/utils";

// Tela inicial (hub), no modelo do app.flamex: saudação com animação e carrossel de avisos.
// Os avisos são cadastrados na administração (Configurações › Avisos do hub) — publicar não exige
// deploy. Cada aviso ocupa a mesma área, em proporção 3:1, e a imagem preenche o cartão (o excesso
// é cortado nas bordas): arte ideal larga, por volta de 1600x520.
const AUTOPLAY_DELAY_MS = 6000;

function getPrimeiroNome(usuario) {
  const nome = String(usuario?.nome || "").trim();
  if (!nome) return "";
  return nome.split(/\s+/)[0];
}

export default function Hub() {
  const [usuario, setUsuario] = useState(null);
  const [avisos, setAvisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const autoplay = useMemo(
    () => Autoplay({ delay: AUTOPLAY_DELAY_MS, stopOnInteraction: false, stopOnMouseEnter: true }),
    []
  );
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center", duration: 34 }, [autoplay]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const [user, lista] = await Promise.all([getUsuarioLogado(), listarAvisosVigentes()]);
        if (!mounted) return;
        setUsuario(user);
        setAvisos(lista);
      } catch {
        // Hub não bloqueia o acesso ao sistema: sem avisos, mostra só a saudação.
        if (mounted) setAvisos([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!emblaApi) return undefined;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const primeiroNome = getPrimeiroNome(usuario);
  const temVariosAvisos = avisos.length > 1;

  return (
    <div className="space-y-6">
      <section className="flex items-center gap-2" aria-label="Boas-vindas">
        <h1 className="font-display text-xl font-bold tracking-tight text-foreground sm:text-2xl">
          Olá{primeiroNome ? ", " : ""}
          <span className="text-gold">{primeiroNome}</span>. Bora movimentar negócios?
        </h1>
        <span className="h-9 w-9 shrink-0" aria-hidden="true">
          <Lottie animationData={moneyBagAnimation} loop autoplay />
        </span>
      </section>

      <section aria-label="Avisos">
        {loading ? (
          <Skeleton className="aspect-[3/1] w-full rounded-2xl" />
        ) : avisos.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border px-6 py-12 text-center">
            <Megaphone className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm font-medium text-foreground">Nenhum aviso publicado</p>
            <p className="text-sm text-muted-foreground">
              Os avisos aparecem aqui assim que forem cadastrados em Configurações.
            </p>
          </div>
        ) : (
          // Sangra até a borda do conteúdo (anula o padding do .app-shell): sem isso o vizinho
          // termina antes da margem e aparece um vazio nas pontas, em vez de ele ser cortado pela
          // tela — que é o que dá a sensação de que sempre há um anterior e um próximo.
          <div className="relative -mx-4 sm:-mx-6 lg:-mx-10">
            {/* py-3: o cartão central fica em escala cheia e os vizinhos reduzidos — sem esse respiro
                a sombra do ativo seria cortada em cima e embaixo. */}
            <div className="overflow-hidden py-3" ref={emblaRef}>
              <div className="flex">
                {avisos.map((aviso, index) => {
                  const ativo = index === selectedIndex;
                  // Posição relativa ao slide ativo, considerando a volta do laço (o último é
                  // "anterior" ao primeiro). Define para que lado o vizinho encolhe.
                  let distancia = index - selectedIndex;
                  if (distancia > avisos.length / 2) distancia -= avisos.length;
                  if (distancia < -avisos.length / 2) distancia += avisos.length;
                  const conteudo = (
                    <article
                      className={cn(
                        // Central em tamanho cheio, vizinhos menores e apagados: mostra que há um
                        // anterior e um próximo sem competir com o aviso da vez. O vizinho encolhe
                        // PARA FORA (origin na borda de dentro), senão ele se afasta do centro dos
                        // dois lados e abre um vão enorme.
                        "relative aspect-[3/1] w-full overflow-hidden rounded-[28px] border border-border bg-muted transition-all duration-300 ease-out",
                        ativo ? "scale-100 opacity-100 shadow-lg" : "scale-[0.92] opacity-45 saturate-50 shadow-sm",
                        ativo ? "origin-center" : distancia < 0 ? "origin-right" : "origin-left"
                      )}
                    >
                      {aviso.imagemUrl ? (
                        <img
                          src={aviso.imagemUrl}
                          alt={aviso.somenteImagem ? aviso.titulo : ""}
                          className="absolute inset-0 h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : null}

                      {aviso.somenteImagem ? null : (
                        <>
                          {/* Véu só do lado do texto, para o título ficar legível sobre qualquer arte. */}
                          <div className="absolute inset-0 bg-gradient-to-r from-kpi-ink/85 via-kpi-ink/60 to-transparent" />
                          <div className="relative flex h-full max-w-[56%] flex-col justify-center gap-1.5 p-5 text-kpi-foreground sm:p-8">
                            {aviso.etiqueta ? (
                              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-kpi-foreground/85">
                                {aviso.etiqueta}
                              </span>
                            ) : null}
                            <h2 className="font-display text-lg font-semibold leading-tight sm:text-2xl">{aviso.titulo}</h2>
                            {aviso.descricao ? (
                              <p className="line-clamp-3 text-xs text-kpi-foreground/85 sm:text-sm">{aviso.descricao}</p>
                            ) : null}
                            {aviso.linkDestino && aviso.acaoLabel ? (
                              <span className="mt-2 inline-flex w-fit items-center gap-1.5 rounded-lg bg-action px-3 py-1.5 text-xs font-semibold text-action-foreground">
                                {aviso.acaoLabel}
                                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </div>
                        </>
                      )}
                    </article>
                  );

                  return (
                    <div key={aviso.id} className="min-w-0 shrink-0 grow-0 basis-[88%] px-1.5">
                      {aviso.linkDestino ? (
                        <Link
                          to={aviso.linkDestino}
                          className="block rounded-[28px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {conteudo}
                        </Link>
                      ) : (
                        conteudo
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {temVariosAvisos ? (
              <>
                {/* Faixa com a largura do cartão central (mesmo 88% do slide): sem ela as setas
                    ficariam nas pontas da tela, sobre os vizinhos, porque o carrossel sangra até a
                    borda. pointer-events-none na faixa para não bloquear o arraste do carrossel. */}
                <div className="pointer-events-none absolute inset-y-0 left-1/2 w-[88%] -translate-x-1/2">
                  <button
                    type="button"
                    onClick={scrollPrev}
                    aria-label="Aviso anterior"
                    className="pointer-events-auto absolute left-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-popover text-foreground shadow-lg transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:inline-flex"
                  >
                    <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={scrollNext}
                    aria-label="Próximo aviso"
                    className="pointer-events-auto absolute right-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-popover text-foreground shadow-lg transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:inline-flex"
                  >
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-4 flex items-center justify-center gap-2" aria-label="Selecionar aviso">
                  {avisos.map((aviso, index) => (
                    <button
                      key={`ponto-${aviso.id}`}
                      type="button"
                      onClick={() => emblaApi?.scrollTo(index)}
                      aria-label={`Ir para o aviso ${index + 1}`}
                      aria-current={index === selectedIndex ? "true" : undefined}
                      className={cn(
                        "h-2 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        index === selectedIndex ? "w-6 bg-action" : "w-2 bg-border hover:bg-foreground/40"
                      )}
                    />
                  ))}
                </div>
              </>
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}
